import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Error404.css";

import bg1 from "../assets/images/building.webp";
import bg2 from "../assets/images/buildingconstruct.webp";
import bg3 from "../assets/images/civil.webp";
import bg4 from "../assets/images/swm.webp";

const TextReveal = ({ children = "", delay = 0, animation }) => {
  const animations = {
    typewriter: {
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.1, delay },
    },
    fadeUp: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.2, delay },
    },
    slideIn: {
      initial: { opacity: 0, x: -20 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.15, delay },
    },
    scaleUp: {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.2, delay },
    },
  };

  const selectedAnimation = animations[animation || "typewriter"];
  const textArray = String(children).split("");

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, delay }}
      style={{ display: "inline-block" }}
    >
      {textArray.map((char, index) => (
        <motion.span
          key={index}
          initial={selectedAnimation.initial}
          animate={selectedAnimation.animate}
          transition={{
            ...selectedAnimation.transition,
            delay: delay + index * 0.01,
          }}
          style={{
            display: "inline-block",
            whiteSpace: "pre",
          }}
        >
          {char}
        </motion.span>
      ))}
    </motion.div>
  );
};

const SimpleText = ({ text = "404" }) => {
  return (
    <div className="error-404">
      <h1 style={{
        fontSize: '12rem',
        fontWeight: 'bold',
        margin: '0',
        lineHeight: '1'
      }}>{text}</h1>
    </div>
  );
};

const Error404 = () => {
  const backgroundImages = [bg1, bg2, bg3, bg4];
  const errorTitles = [
    "Oops, page not found",
    "Looks like you're lost",
    "This page is on vacation",
    "Houston, we have a problem",
    "404: Page Missing in Action",
    "This is awkward...",
    "Well, this is embarrassing",
  ];

  const errorMessages = [
    "The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.",
    "We've searched high and low, but couldn't find what you're looking for.",
    "Don't worry, it happens to the best of us. Let's get you back on track.",
    "Maybe this page took a wrong turn at Albuquerque?",
    "Seems like this page is playing hide and seek (and winning).",
    "This page has gone on a digital vacation without leaving a forwarding address.",
    "We've looked everywhere, but this page seems to have vanished into thin air.",
  ];

  const buttonTexts = [
    "Back To Homepage",
    "Take Me Home",
    "Return to Safety",
    "Escape Route",
    "Back to Reality",
    "Let's Start Over",
    "Return to Base",
  ];

  const animations = ["typewriter", "fadeUp", "slideIn", "scaleUp"];

  const entranceAnimations = [
    { scale: [0.9, 1], opacity: [0, 1] },
    { y: [-50, 0], opacity: [0, 1] },
    { x: [-50, 0], opacity: [0, 1] },
    { rotate: [-10, 0], opacity: [0, 1] },
  ];

  const [currentTitle, setCurrentTitle] = useState(errorTitles[0]);
  const [currentMessage, setCurrentMessage] = useState(errorMessages[0]);
  const [currentButton, setCurrentButton] = useState(buttonTexts[0]);
  const [currentBackground, setCurrentBackground] = useState(backgroundImages[0]);
  const [currentAnimation, setCurrentAnimation] = useState(animations[0]);

  const [currentEntrance, setCurrentEntrance] = useState(entranceAnimations[0]);
  const [isVisible, setIsVisible] = useState(false);

  const getRandomItem = useCallback((array) => {
    return array[Math.floor(Math.random() * array.length)];
  }, []);

  useEffect(() => {
    setIsVisible(true);
    setCurrentTitle(getRandomItem(errorTitles));
    setCurrentMessage(getRandomItem(errorMessages));
    setCurrentButton(getRandomItem(buttonTexts));
    setCurrentBackground(getRandomItem(backgroundImages));
    setCurrentAnimation(getRandomItem(animations));

    setCurrentEntrance(getRandomItem(entranceAnimations));
  }, [getRandomItem]);

  return (
    <motion.div
      className="not-found-container"
      style={{ backgroundImage: `url(${currentBackground})` }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="content"
            initial={{ opacity: 0 }}
            animate={currentEntrance}
            transition={{ duration: 0.4 }}
          >
            <SimpleText text="404" />

            <motion.div className="error-title">
              <TextReveal delay={0.2} animation={currentAnimation}>
                {currentTitle}
              </TextReveal>
            </motion.div>

            <motion.div className="error-message">
              <TextReveal delay={0.4} animation={currentAnimation}>
                {currentMessage}
              </TextReveal>
            </motion.div>

            <motion.a
              href="/"
              className="back-button"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.6 }}
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.1 },
              }}
              whileTap={{ scale: 0.95 }}
            >
              {currentButton}
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Error404;